import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import { PERSONAL_DATA_VALUES, fromPersonalDataValue } from '../../formMappers'
import useStyles from './styles'

const {
  RISK_CLASS,
  ALERT_CLASS,
  ALERT_ICON,
  NONE_OF_THE_ABOVE_LABEL,
  NONE_OPTION_ID,
  NONE_OPTION,
} = constants.aiInventory

const RiskAnalysis = ({
  isShow = false,
  aiInventoryId = null,
  guestMode = false,
  calculatedRisk = null,
  onSaved = () => {},
  onPersonalDataChange,
  stepLabel = null,
  isActive = true,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { control, errors, watch, getValues, setValue } = useFormContext()
  const processesPersonalData = watch('processesPersonalData')
  const riskOptionIds = watch('riskOptionIds') || []

  const [selectedIds, setSelectedIds] = useState(riskOptionIds)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setSelectedIds(riskOptionIds)
  }, [riskOptionIds.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const riskOptionsService = guestMode
    ? service.guestPortal.aiInventoryRiskOptions.get
    : service.dponet.aiInventoryRiskOptions.get
  const { response: optionsResponse, isLoading: loadingOptions } = useFetch(
    riskOptionsService,
    undefined,
    [isActive],
    isActive,
  )
  const riskOptions = optionsResponse?.data?.aiInventoryRiskOptions || []

  // "Nenhum dos anteriores" é opção apenas de frontend — filtra do backend se vier
  const regularOptions = riskOptions.filter(
    (option) => option.label !== NONE_OF_THE_ABOVE_LABEL,
  )
  const noneChecked = isEmpty(selectedIds)

  const hasPersonalDataAnswer =
    processesPersonalData === PERSONAL_DATA_VALUES.YES ||
    processesPersonalData === PERSONAL_DATA_VALUES.NO
  const showRiskOptions = processesPersonalData === PERSONAL_DATA_VALUES.YES

  const { RISK, RISK_ALERTS } = constants.aiInventory
  const alertInfo = calculatedRisk ? RISK_ALERTS[calculatedRisk] : null
  const AlertIcon = calculatedRisk ? ALERT_ICON[calculatedRisk] : null

  const handleToggle = async (optionId) => {
    if (isShow || !aiInventoryId || saving) return

    const isNoneOption = optionId === NONE_OPTION_ID
    let nextIds

    if (isNoneOption) {
      if (isEmpty(selectedIds)) return // já sem seleção, no-op
      nextIds = []
    } else {
      if (selectedIds.includes(optionId)) {
        // Desmarcando pai → remove também os filhos
        const parent = regularOptions.find((option) => option.id === optionId)
        const childIds = parent?.children?.map((child) => child.id) || []
        nextIds = selectedIds.filter(
          (id) => id !== optionId && !childIds.includes(id),
        )
      } else {
        nextIds = [...selectedIds, optionId]
      }
    }

    const previousIds = [...selectedIds]
    setSelectedIds(nextIds)
    setSaving(true)
    try {
      const updateService = guestMode
        ? service.guestPortal.aiInventory.updateRiskAnalysis
        : service.dponet.aiInventory.updateRiskAnalysis

      const response = await updateService(aiInventoryId, {
        riskOptionIds: nextIds,
        processesPersonalData: fromPersonalDataValue(
          getValues('processesPersonalData'),
        ),
      })

      setValue('riskOptionIds', nextIds)
      const aiInventory = response?.data?.aiInventory
      if (onSaved) onSaved(aiInventory)
    } catch {
      setSelectedIds(previousIds)
      snackbar.open({
        message: 'Erro ao salvar opção de risco. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const renderOptionItem = (option, overrideChecked) => {
    const isChecked =
      overrideChecked !== undefined
        ? overrideChecked
        : selectedIds.includes(option.id)
    return (
      <Grid item xs={12} sm={6} key={option.id}>
        <Box
          className={clsx(classes.optionItem, {
            [classes.optionChecked]: isChecked,
            [classes.optionReadOnly]: isShow,
          })}
          onClick={isShow || saving ? undefined : () => handleToggle(option.id)}
        >
          <Checkbox
            checked={isChecked}
            color="primary"
            size="small"
            tabIndex={-1}
            disabled={isShow || saving}
            onChange={() => {}}
          />
          <Typography variant="body2" className={classes.optionLabel}>
            {option.label}
          </Typography>
        </Box>
      </Grid>
    )
  }

  const visibleChildQuestions = regularOptions
    .filter(
      (option) => selectedIds.includes(option.id) && !isEmpty(option.children),
    )
    .flatMap((option) => option.children)

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="overline">
            <strong>ANALISE DE RISCOS</strong>
          </Typography>
          {stepLabel && (
            <Typography variant="subtitle1">{stepLabel}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary">
          Avalie se o uso da Inteligência Artificial envolve dados pessoais e
          identifique os possíveis fatores de risco.
        </Typography>
      </Box>

      <Divider />

      <Box p={2}>
        <Typography variant="subtitle2" gutterBottom>
          Dados Pessoais
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Você utiliza dados pessoais (por exemplo, de clientes, pacientes,
          colaboradores, etc.) dentro da Inteligência Artificial?
        </Typography>
        <FormControl
          error={!!errors.processesPersonalData}
          disabled={isShow || !aiInventoryId}
        >
          <Controller
            name="processesPersonalData"
            control={control}
            onChange={([event]) => {
              const value = event.target.value
              if (aiInventoryId && onPersonalDataChange) {
                onPersonalDataChange(value)
              }
              return value
            }}
            as={
              <RadioGroup row>
                <FormControlLabel
                  value={PERSONAL_DATA_VALUES.YES}
                  control={<Radio color="primary" size="small" />}
                  label="Sim"
                />
                <FormControlLabel
                  value={PERSONAL_DATA_VALUES.NO}
                  control={<Radio color="primary" size="small" />}
                  label="Não"
                />
              </RadioGroup>
            }
          />
          {!!errors.processesPersonalData && (
            <FormHelperText>
              {errors.processesPersonalData?.message}
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {showRiskOptions && (
        <>
          <Divider />
          <Box p={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography variant="subtitle2">
                Tratamento em categorias sensíveis
              </Typography>
              {saving && <CircularProgress size={16} />}
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Os dados pessoais utilizados na Inteligência Artificial se
              relacionam a alguns dos seguintes tópicos? (múltipla escolha)
            </Typography>
            {loadingOptions ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box mt={1}>
                <Grid container spacing={1}>
                  {regularOptions.map((option) => renderOptionItem(option))}
                  {renderOptionItem(NONE_OPTION, noneChecked)}
                </Grid>
              </Box>
            )}
          </Box>

          {visibleChildQuestions.map((child) => {
            const isYes = selectedIds.includes(child.id)
            return (
              <React.Fragment key={child.id}>
                <Divider />
                <Box p={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {child.label}
                  </Typography>
                  {child.question && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {child.question}
                    </Typography>
                  )}
                  <FormControl disabled={isShow || !aiInventoryId || saving}>
                    <RadioGroup
                      row
                      value={isYes ? 'yes' : 'no'}
                      onChange={(event) => {
                        const answer = event.target.value
                        const currentlyYes = selectedIds.includes(child.id)
                        if (answer === 'yes' && !currentlyYes)
                          handleToggle(child.id)
                        if (answer === 'no' && currentlyYes)
                          handleToggle(child.id)
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" size="small" />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="primary" size="small" />}
                        label="Não"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </React.Fragment>
            )
          })}
        </>
      )}

      {calculatedRisk && !guestMode && hasPersonalDataAnswer && (
        <>
          <Divider />
          <Box p={2}>
            <Box
              display="flex"
              alignItems="center"
              gridGap={8}
              mb={alertInfo ? 2 : 0}
            >
              <Typography variant="subtitle2">Nível do Risco</Typography>
              <Chip
                label={RISK[calculatedRisk] || calculatedRisk}
                className={classes[RISK_CLASS[calculatedRisk]]}
                size="small"
              />
            </Box>
            {alertInfo && (
              <Box
                className={clsx(
                  classes.alert,
                  classes[ALERT_CLASS[calculatedRisk]],
                )}
              >
                <Box className={classes.alertHeader}>
                  <AlertIcon size={20} className={classes.alertIcon} />
                  <Typography variant="body2" className={classes.alertTitle}>
                    {alertInfo.label}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  className={classes.alertDescription}
                >
                  {alertInfo.description}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  )
}

RiskAnalysis.propTypes = {
  isShow: PropTypes.bool,
  aiInventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  guestMode: PropTypes.bool,
  calculatedRisk: PropTypes.string,
  onSaved: PropTypes.func,
  onPersonalDataChange: PropTypes.func,
  stepLabel: PropTypes.string,
  isActive: PropTypes.bool,
}

export default RiskAnalysis
